import React from 'react';
import { Box, ButtonBase, InputAdornment, TextField, Tooltip, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CopyButton, CustomIcon, NumberFormatCustom } from '../../../custom-forms';
import { cx, StyleClasses } from '../../../custom-forms';
import { CaseCalculationPart } from '../../../interfaces/Case';
import { IReferential } from '../../../interfaces/IReferential';
import { WorkflowCalculation, WorkflowSupplier } from '../../../interfaces/Workflow';
import { CalculationActions, ColumnKey } from '../types/alternative-calculation.types';
import { COLUMN_WIDTH, GridCellCtx, GridColumnConfig } from './columns';
import {
  displayAmount,
  isNonStockPartSelected,
  queriedSuppliersCount
} from '../utils/calculation.utils';
import { SUPPLIERS_TOTAL } from '../../../helpers/Constants';
import AlternativeCalculationPartStatus from '../components/AlternativeCalculationPartStatus';
import { VariantType } from 'notistack';

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export interface AltCalcGridColumnsCtx {
  workflowCalculation: WorkflowCalculation | null;
  qualities: IReferential[];
  actions: CalculationActions;
  onCopyPart: (part: CaseCalculationPart) => void;
  onNotify: (message: string, variant: VariantType) => void;
  c: StyleClasses;
  t: (key: string, fallback: string) => string;
  country: IReferential | null;
  variantsEnabled: boolean;
  showNetPrice: boolean;
  caseIsOffline: boolean;
  nonStockSuppliers: WorkflowSupplier[];
}

export const buildAltCalcGridColumns = (ctx: AltCalcGridColumnsCtx): GridColumnConfig[] => {
  const {
    workflowCalculation,
    qualities,
    actions,
    onNotify,
    c,
    t,
    country,
    variantsEnabled,
    showNetPrice,
    caseIsOffline,
    nonStockSuppliers
  } = ctx;
  const showDescription = workflowCalculation?.hasAlternativePartDescription ?? false;

  const spacer = (
    key: string,
    header: Pick<GridColumnConfig, 'sortKey' | 'filterable' | 'numeric' | 'renderHeaderCell'>
  ): GridColumnConfig => ({
    key,
    width: COLUMN_WIDTH[key],
    ...header,
    renderBodyCell: () => <Box />
  });

  const columns: GridColumnConfig[] = [
    {
      key: 'code',
      width: COLUMN_WIDTH.code,
      sortKey: ColumnKey.Code,
      filterable: true,
      numeric: false,
      renderHeaderCell: () =>
        t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_CODE_LABEL', 'Cod'),
      renderBodyCell: ({ part }: GridCellCtx) => {
        return (
          <Box className={c.minW0}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              key={`${part.id}-code`}
              style={{ marginBottom: 0 }}
              disabled={workflowCalculation?.editGuideNumber === false}
              defaultValue={part.guideNumber}
              placeholder={t('ALT_CALC_PART_CODE', 'cod piesă')}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                actions.updateGuideNumber(part.id, e.target.value)
              }
              InputProps={{
                endAdornment: workflowCalculation?.addOriginalPart ? (
                  <InputAdornment position="end" style={{ marginLeft: 2 }}>
                    <CopyButton
                      value={part.guideNumber ?? ''}
                      tooltip={t('ALT_CALC_COPY_CODE', 'Click pentru a copia codul')}
                      onCopied={(code) => onNotify(t('ALT_CALC_CODE_COPIED', 'Cod copiat: ') + code, 'info')}
                    />
                  </InputAdornment>
                ) : undefined
              }}
            />
          </Box>
        );
      }
    },
    {
      key: 'name',
      width: COLUMN_WIDTH.name,
      sortKey: ColumnKey.Name,
      filterable: true,
      numeric: false,
      renderHeaderCell: () => t('ORDER_PART_DETAILS_NAME_LABEL', 'Denumire'),
      renderBodyCell: ({ part }: GridCellCtx) => (
        <Box
          className={c.minW0}
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8
          }}
        >
          <Box className={cx(c.row, c.gap8, c.minW0)}>
            <Typography variant="body2" className={c.semibold}>
              {part.name}
            </Typography>
            <AlternativeCalculationPartStatus part={part} />
          </Box>
          {showDescription && (
            <Box style={{ flex: '1 1 160px', minWidth: 160 }}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginBottom: 0 }}
                // Editable only once a non-stock part is selected for this position.
                disabled={!isNonStockPartSelected(part, nonStockSuppliers)}
                error={part.descriptionError}
                label={t(
                  'CASE_CALCULATION_ALTERNATIVE_ALTERNATIVE_DESCRIPTION_LABEL',
                  'Descriere piesa alt'
                )}
                value={part.alternativePartDescription ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  actions.updateAlternativePartDescription(part.id, e.target.value)
                }
              />
            </Box>
          )}
        </Box>
      )
    },
    ...(showNetPrice
      ? [
          spacer('netPrice', {
            sortKey: ColumnKey.NetPrice,
            filterable: true,
            numeric: true,
            renderHeaderCell: () => t('CALCULATION_DETAILS_NET_PRICE_LKQ_HEADER', 'Pret Net')
          })
        ]
      : []),
    {
      key: 'price',
      width: COLUMN_WIDTH.price,
      sortKey: ColumnKey.Price,
      filterable: true,
      numeric: true,
      renderHeaderCell: () =>
        t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_PRICE_LABEL', 'Pret'),
      renderBodyCell: ({ part, onPriceChange }: GridCellCtx) => {
        const priceEditable = part.editOriginalPrice || caseIsOffline;
        const discountedPrice =
          Math.round(part.originalPrice * (1 - part.discount / 100) * 100) / 100;
        return (
          <Box>
            {priceEditable ? (
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginBottom: 0 }}
                error={part.priceError}
                value={part.originalPrice ?? ''}
                InputProps={{ inputComponent: NumberFormatCustom }}
                inputProps={{ style: { textAlign: 'right' } }}
                onChange={onPriceChange}
              />
            ) : (
              <Typography variant="body2" className={cx(c.right, c.muted, c.tabular)}>
                {displayAmount(discountedPrice, country)}
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      key: 'quality',
      width: COLUMN_WIDTH.quality,
      sortKey: ColumnKey.Quality,
      filterable: true,
      numeric: false,
      renderHeaderCell: () =>
        t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_QUALITY_LABEL_ACRONYM', 'CAL.'),
      renderBodyCell: ({ part }: GridCellCtx) => {
        const quality = part.carPartCategory ? part.carPartCategory.code : '';
        return (
          <Box style={{ textAlign: 'center' }}>
            {caseIsOffline ? (
              <Box onClick={stop}>
                <Autocomplete
                  fullWidth
                  options={qualities}
                  value={part.carPartCategory ?? null}
                  disableClearable
                  getOptionLabel={(option: IReferential) => option.code ?? ''}
                  onChange={(_e: React.SyntheticEvent, newValue: IReferential | null) =>
                    actions.updateCarPartCategory(part.id, newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" fullWidth />
                  )}
                />
              </Box>
            ) : quality ? (
              <Box component="span" className={cx(c.badge, c.badgeOutline)}>
                {quality}
              </Box>
            ) : null}
          </Box>
        );
      }
    },
    spacer('delivery', {
      sortKey: ColumnKey.Delivery,
      filterable: true,
      numeric: false,
      renderHeaderCell: () =>
        t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_DELIVERY_LABEL', 'Livrare')
    }),
    spacer('producer', {
      sortKey: ColumnKey.Producer,
      filterable: true,
      numeric: false,
      renderHeaderCell: () =>
        t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_OWNER_LABEL_ACRONYM', 'Producator')
    }),
    {
      key: 'supplier',
      width: COLUMN_WIDTH.supplier,
      sortKey: ColumnKey.Supplier,
      filterable: true,
      numeric: false,
      renderHeaderCell: () =>
        t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_SUPLIER_LABEL', 'Furnizor'),
      renderBodyCell: ({ part }: GridCellCtx) => (
        <Tooltip title={t('ALT_CALC_SUPPLIERS', 'furnizori')}>
          <Box component="span" className={cx(c.badge, c.badgeOutline)} style={{ justifySelf: 'start' }}>
            {queriedSuppliersCount(part)}/{SUPPLIERS_TOTAL}
          </Box>
        </Tooltip>
      )
    },
    {
      key: 'quantity',
      width: COLUMN_WIDTH.quantity,
      sortKey: ColumnKey.Quantity,
      filterable: true,
      numeric: true,
      renderHeaderCell: () =>
        t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_QUANTITY_INFO_LABEL', 'Cant.'),
      renderBodyCell: ({ part, onQuantityChange }: GridCellCtx) => (
        <Box>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            style={{ marginBottom: 0 }}
            error={part.unitsError}
            disabled={workflowCalculation?.editQuantity === false}
            InputProps={{ inputComponent: NumberFormatCustom }}
            inputProps={{ style: { textAlign: 'right' } }}
            value={part.pieceNr == null ? '' : part.pieceNr}
            onChange={onQuantityChange}
          />
        </Box>
      )
    },
    ...(variantsEnabled
      ? [
          spacer('location', {
            sortKey: ColumnKey.Location,
            filterable: false,
            numeric: false,
            renderHeaderCell: () =>
              t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_STOCK_LOCATION_LABEL', 'Locatie')
          })
        ]
      : []),
    {
      key: 'actions',
      width: COLUMN_WIDTH.actions,
      sortKey: variantsEnabled ? ColumnKey.Difference : ColumnKey.Location,
      filterable: false,
      numeric: variantsEnabled,
      renderHeaderCell: () =>
        variantsEnabled
          ? t('ALT_CALC_COL_DIFF', 'Diferență')
          : t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_STOCK_LOCATION_LABEL', 'Locatie'),
      renderBodyCell: ({
        part,
        onQuery,
        onOpenNotify,
        onAddAlternativePart,
        onDeletePart,
        infoTooltip
      }: GridCellCtx) => (
        <Box className={cx(c.row, c.justifyEnd, c.gap8)}>
          {infoTooltip ? (
            <Tooltip title={infoTooltip}>
              <Box
                component="span"
                style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <CustomIcon name="info" />
              </Box>
            </Tooltip>
          ) : null}
          <Tooltip title={t('ALT_CALC_QUERY_SUPPLIERS', 'Interoghează furnizorii')} placement="top">
            <ButtonBase className={cx(c.iconBtn, c.iconBtnSm, c.iconBtnPrimary)} onClick={onQuery}>
              <CustomIcon name="refresh" />
            </ButtonBase>
          </Tooltip>
          {workflowCalculation?.addNotifySupportOriginalPart ? (
            <Tooltip
              title={t('ALT_CALC_SEND_SUPPORT', 'Trimite mesaj către support')}
              placement="top"
            >
              <ButtonBase
                className={cx(c.iconBtn, c.iconBtnSm)}
                onClick={(e) => {
                  stop(e);
                  onOpenNotify && onOpenNotify();
                }}
              >
                <CustomIcon name="mail" />
              </ButtonBase>
            </Tooltip>
          ) : null}
          {workflowCalculation?.addAlternativePart ? (
            <Tooltip
              title={t('ALT_CALC_ADD_ALTERNATIVE', 'Adaugă piesa alternativă')}
              placement="top"
            >
              <ButtonBase
                className={cx(c.iconBtn, c.iconBtnSm)}
                onClick={(e) => {
                  stop(e);
                  onAddAlternativePart && onAddAlternativePart();
                }}
              >
                <CustomIcon name="plus" />
              </ButtonBase>
            </Tooltip>
          ) : null}
          {workflowCalculation?.deleteOriginalPart ? (
            <Tooltip
              title={t('ALT_CALC_DELETE_PART', 'Șterge piesa din calculație')}
              placement="top"
            >
              <ButtonBase
                className={cx(c.iconBtn, c.iconBtnSm, c.iconBtnDanger)}
                onClick={onDeletePart}
              >
                <CustomIcon name="trash" />
              </ButtonBase>
            </Tooltip>
          ) : null}
        </Box>
      )
    }
  ];

  return columns;
};
